import type { PresentationModification, ShapeModification } from "../../types";
import { hasAnyImagePayload } from "../../utils/imagePayload";
import type { AgentToolContext } from "./types";

export type LogoUrlResultLike = {
  success?: boolean;
  companyName?: string;
  logoUrl?: string;
  needsReview?: boolean;
  candidates?: Array<{
    logoCandidateRef?: string;
    logoUrl?: string;
  }>;
};

export function normalizeLogoCompanyKey(name: string | undefined | null): string {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function rememberLogoUrls(
  ctx: AgentToolContext | undefined,
  results: readonly LogoUrlResultLike[] | undefined
): void {
  if (!ctx || !Array.isArray(results) || results.length === 0) return;
  for (const result of results) {
    if (!result?.success) continue;

    for (const candidate of result.candidates || []) {
      const logoCandidateRef =
        typeof candidate?.logoCandidateRef === "string"
          ? candidate.logoCandidateRef.trim()
          : "";
      const candidateLogoUrl =
        typeof candidate?.logoUrl === "string" ? candidate.logoUrl.trim() : "";
      if (!logoCandidateRef || !candidateLogoUrl) continue;
      if (!ctx.logoUrlsByCandidateRef) {
        ctx.logoUrlsByCandidateRef = new Map();
      }
      ctx.logoUrlsByCandidateRef.set(logoCandidateRef, candidateLogoUrl);
    }

    // Preserve fail-closed behavior for legacy single-candidate responses during
    // rolling deployments. New candidate-list responses are selected by ref.
    if (result.needsReview) continue;

    const companyName = typeof result.companyName === "string" ? result.companyName.trim() : "";
    const logoUrl = typeof result.logoUrl === "string" ? result.logoUrl.trim() : "";
    if (!companyName || !logoUrl) continue;
    if (!ctx.logoUrlsByCompanyKey) {
      ctx.logoUrlsByCompanyKey = new Map();
    }
    const key = normalizeLogoCompanyKey(companyName);
    if (!key) continue;
    ctx.logoUrlsByCompanyKey.set(key, logoUrl);
  }
}

export function resolveLogoUrl(
  ctx: AgentToolContext | undefined,
  companyName: string | undefined | null
): string | null {
  const key = normalizeLogoCompanyKey(companyName);
  if (!key || !ctx?.logoUrlsByCompanyKey) return null;
  return ctx.logoUrlsByCompanyKey.get(key) ?? null;
}

export function resolveLogoCandidateUrl(
  ctx: AgentToolContext | undefined,
  logoCandidateRef: string | undefined | null
): string | null {
  const ref = String(logoCandidateRef || "").trim();
  if (!ref || !ctx?.logoUrlsByCandidateRef) return null;
  return ctx.logoUrlsByCandidateRef.get(ref) ?? null;
}

function toTextPlaceholder(shape: ShapeModification, companyName: string): void {
  shape.text = companyName;
  delete shape.logoCompanyName;
  delete shape.logoCandidateRef;
  delete shape.imageUrl;
  delete shape.imageDataUri;
  delete shape.imageBase64;
  delete shape.imageMimeType;
  if (typeof shape.fontSize !== "number") {
    shape.fontSize = 8;
  }
}

/**
 * Resolve `logoCompanyName` create shapes to `imageUrl` using the turn cache.
 * Prefer existing image payloads. On miss, convert to a text placeholder.
 */
export function expandLogoCompanyNames(
  modifications: PresentationModification,
  ctx: AgentToolContext | undefined,
  log?: (message: string) => void
): PresentationModification {
  if (!modifications.slides || modifications.slides.length === 0) {
    return modifications;
  }

  let expanded = 0;
  let placeholders = 0;

  for (const slide of modifications.slides) {
    for (const shape of slide.shapes || []) {
      if (shape.action !== "create") continue;
      const logoCompanyName =
        typeof shape.logoCompanyName === "string" ? shape.logoCompanyName.trim() : "";
      const logoCandidateRef =
        typeof shape.logoCandidateRef === "string" ? shape.logoCandidateRef.trim() : "";
      if (!logoCompanyName && !logoCandidateRef) continue;
      if (hasAnyImagePayload(shape)) {
        // Explicit image payload wins; drop the short ref to avoid confusion downstream.
        delete shape.logoCompanyName;
        delete shape.logoCandidateRef;
        continue;
      }

      const logoUrl = logoCandidateRef
        ? resolveLogoCandidateUrl(ctx, logoCandidateRef)
        : resolveLogoUrl(ctx, logoCompanyName);
      if (logoUrl) {
        shape.imageUrl = logoUrl;
        delete shape.logoCompanyName;
        delete shape.logoCandidateRef;
        expanded += 1;
        continue;
      }

      log?.(
        `[apply_edits] company logo ref "${logoCandidateRef || logoCompanyName}" not found in turn logo cache; using text placeholder`
      );
      toTextPlaceholder(shape, logoCompanyName || "Company");
      placeholders += 1;
    }
  }

  if (expanded > 0 || placeholders > 0) {
    log?.(
      `[apply_edits] logoCompanyName expansion: ${expanded} resolved, ${placeholders} placeholder(s)`
    );
  }

  return modifications;
}

/** Strip logo URLs and confidence scores from model-facing logo candidates. */
export function sanitizeCompanyLogoToolData(data: unknown): unknown {
  if (!data || typeof data !== "object") return data;
  const record = data as Record<string, unknown>;
  const {
    logoUrl: _topLogoUrl,
    debugLog: _debugLog,
    confidenceScore: _topConfidenceScore,
    ...rest
  } = record;
  const next: Record<string, unknown> = { ...rest };

  if (Array.isArray(record.results)) {
    next.results = record.results.map((entry) => {
      if (!entry || typeof entry !== "object") return entry;
      const result = entry as Record<string, unknown>;
      const {
        logoUrl: _logoUrl,
        debugLog: _resultDebugLog,
        confidenceScore: _confidenceScore,
        ...resultRest
      } = result;
      if (Array.isArray(result.candidates)) {
        resultRest.candidates = result.candidates.map((candidate) => {
          if (!candidate || typeof candidate !== "object") return candidate;
          const candidateRecord = candidate as Record<string, unknown>;
          const {
            logoUrl: _candidateLogoUrl,
            debugLog: _candidateDebugLog,
            confidenceScore: _candidateConfidenceScore,
            ...candidateRest
          } = candidateRecord;
          return candidateRest;
        });
      }
      return resultRest;
    });
  }

  return next;
}

/** Sanitize tool results before serializing them back into model context. */
export function sanitizeToolResultsForModelOutput<T extends { tool?: string; data?: unknown }>(
  toolResults: T[]
): T[] {
  return toolResults.map((result) => {
    if (!result.data || typeof result.data !== "object") return result;
    if (result.tool === "get_company_logo") {
      return { ...result, data: sanitizeCompanyLogoToolData(result.data) };
    }
    if ("debugLog" in (result.data as Record<string, unknown>)) {
      const { debugLog: _debugLog, ...rest } = result.data as Record<string, unknown>;
      return { ...result, data: rest };
    }
    return result;
  });
}
